import * as React from "react"
import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { Autocomplete } from "@/components/ui/autocomplete"
import { clean } from "@/components/Pagination"
import { useUserSearch } from "@/hooks/use-user-search"
import type { ChangeLogFilterOptions, ChangeLogFilterValues } from "@/types/change_log"

const KIND_LABELS: Record<string, string> = {
  all: "Anything",
  created: "Created",
  updated: "Updated",
  deleted: "Deleted",
  archived: "Archived",
  reactivated: "Reactivated",
  signed_in: "Signed in",
  signed_out: "Signed out",
  tested: "Tested",
}

export function ChangeLogFilters({
  filters,
  options,
}: {
  filters: ChangeLogFilterValues
  options: ChangeLogFilterOptions
}) {
  // The box shows whoever is selected, so clearing it clears the filter.
  const [person, setPerson] = React.useState(options.selected_person?.name ?? "")
  const { suggestions, loading } = useUserSearch(person)

  React.useEffect(() => {
    setPerson(options.selected_person?.name ?? "")
  }, [options.selected_person?.id, options.selected_person?.name])

  // Filtering always returns to page one — staying on page 7 of a list that
  // just shrank to two pages is never what someone meant.
  const apply = (changes: Partial<Record<string, string | number | null>>) => {
    router.get(
      "/admin/change_log",
      clean({
        module: filters.module === "all" ? null : filters.module,
        kind: filters.kind === "all" ? null : filters.kind,
        user_id: filters.user_id,
        from: filters.from,
        to: filters.to,
        ...changes,
      }),
      { preserveState: true, preserveScroll: true, replace: true },
    )
  }

  const isFiltered =
    filters.module !== "all" ||
    filters.kind !== "all" ||
    filters.user_id !== null ||
    Boolean(filters.from) ||
    Boolean(filters.to)

  return (
    <div className="callout mb-6 py-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Module" htmlFor="filter_module">
          <Select
            id="filter_module"
            value={filters.module}
            onChange={(event) => apply({ module: event.target.value === "all" ? null : event.target.value })}
          >
            <option value="all">Every module</option>
            {options.modules.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="What happened" htmlFor="filter_kind">
          <Select
            id="filter_kind"
            value={filters.kind}
            onChange={(event) => apply({ kind: event.target.value === "all" ? null : event.target.value })}
          >
            {options.kinds.map((kind) => (
              <option key={kind} value={kind}>
                {KIND_LABELS[kind] ?? kind}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Person" htmlFor="filter_person">
          <Autocomplete
            id="filter_person"
            value={person}
            onValueChange={(value) => {
              setPerson(value)
              // Emptying the box is how you clear the filter; typing on its own
              // does not filter, because a half-typed name is not a person.
              if (value.trim() === "" && filters.user_id !== null) apply({ user_id: null })
            }}
            suggestions={suggestions.map((found) => ({
              id: found.id,
              label: found.name,
              hint: [found.email, found.organization].filter(Boolean).join(" · ") || null,
            }))}
            loading={loading}
            onSelect={(suggestion) => apply({ user_id: Number(suggestion.id) })}
            placeholder="Anybody"
            hintMessage="Type a name to narrow to one person."
            emptyMessage="No accounts match."
            aria-label="Filter by person"
          />
        </Field>

        <Field label="From" htmlFor="filter_from">
          <Input
            id="filter_from"
            type="date"
            value={filters.from ?? ""}
            onChange={(event) => apply({ from: event.target.value || null })}
          />
        </Field>

        <Field label="To" htmlFor="filter_to">
          <Input
            id="filter_to"
            type="date"
            value={filters.to ?? ""}
            onChange={(event) => apply({ to: event.target.value || null })}
          />
        </Field>

        {isFiltered && (
          <div className="flex items-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPerson("")
                router.get("/admin/change_log", {}, { preserveScroll: true, replace: true })
              }}
            >
              Clear filters
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label htmlFor={htmlFor}>{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  )
}
